import React, { useState } from "react";
import { Loader, Calendar, Copy } from "lucide-react";
import { api, friendlyAxiosError } from "../../lib/api";
import ProgressMeter from "../ProgressMeter";
import ResultSection from "../ResultSection";

export default function EventsPanel() {
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [interests, setInterests] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/network/events-suggest", {
        role: role.trim() || null,
        location: location.trim() || null,
        interests: interests.trim() || null,
      });
      setData(data);
    } catch (e) {
      setError(friendlyAxiosError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="events-panel">
      <div className="rm-card-static p-5 md:p-6">
        <div className="rm-label mb-1" style={{ color: "#FF6A00" }}>· Show up where it matters</div>
        <h2 className="font-display text-2xl uppercase mb-3">Find the rooms.</h2>
        <p className="text-sm mb-4" style={{ color: "#475569" }}>
          Resmo names the real communities and event types in your space.
          Eventbrite/Meetup direct feeds are coming — for now, copy the search
          queries Resmo gives you.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input className="rm-input" placeholder="Your role (e.g. Senior PM)" value={role}
                 onChange={(e) => setRole(e.target.value)} data-testid="events-role" />
          <input className="rm-input" placeholder="Location (e.g. NYC, Remote)" value={location}
                 onChange={(e) => setLocation(e.target.value)} data-testid="events-location" />
          <input className="rm-input" placeholder="Interests (e.g. AI infra, climate)" value={interests}
                 onChange={(e) => setInterests(e.target.value)} data-testid="events-interests" />
        </div>
        {error && <div className="mt-3 font-mono-rm text-sm" style={{ color: "#991B1B" }} data-testid="events-error">{error}</div>}
        <button onClick={submit} disabled={loading} className="rm-btn rm-btn-primary mt-4" data-testid="events-submit-btn">
          {loading ? <Loader size={16} className="animate-spin" /> : <Calendar size={16} />}
          {loading ? "Scanning…" : "Suggest communities (5 credits)"}
        </button>
        {loading && (
          <div className="mt-3 max-w-sm">
            <ProgressMeter expectedSeconds={20} label="Scanning communities" testid="events-progress-meter" />
          </div>
        )}
      </div>

      {data && (
        <div className="space-y-4" data-testid="events-results">
          {(data.communities || []).length > 0 && (
            <ResultSection
              title="Real communities to join"
              eyebrow="Show up where it matters"
              accentGradient="linear-gradient(135deg,#FF6A00 0%,#B45309 100%)"
              count={data.communities.length}
              defaultOpen
              testid="events-communities-section"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.communities.map((c, i) => (
                  <div key={i} className="p-3 rounded-md" style={{ border: "2px solid #0F1B2D", background: "white" }}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display text-base uppercase leading-tight">{c.name}</div>
                      <span className="font-mono-rm text-[9px] uppercase tracking-widest px-2 py-0.5"
                            style={{ background: "#FFFBEB", border: "2px solid #FFC145", color: "#92400E" }}>{c.type}</span>
                    </div>
                    <p className="text-sm mt-1.5" style={{ color: "#475569" }}>{c.why}</p>
                    {c.find_at && (
                      <div className="font-mono-rm text-[10px] mt-1.5" style={{ color: "#0F1B2D" }}>
                        Search at: <span className="font-bold">{c.find_at}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ResultSection>
          )}
          {(data.events_to_search || []).length > 0 && (
            <ResultSection
              title="Search queries to run today"
              eyebrow="Copy → paste → discover"
              accentGradient="linear-gradient(135deg,#FFC145 0%,#CA8A04 100%)"
              count={data.events_to_search.length}
              testid="events-queries-section"
            >
              <ul className="space-y-2">
                {data.events_to_search.map((e, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 p-2 rounded"
                      style={{ background: "#FFFBEB", border: "2px dashed #FFC145" }}>
                    <div>
                      <div className="font-semibold text-sm">"{e.search_query}"</div>
                      <div className="font-mono-rm text-[10px] uppercase tracking-widest mt-0.5" style={{ color: "#92400E" }}>
                        on {e.platform} · {e.cadence}
                      </div>
                    </div>
                    <button onClick={() => navigator.clipboard.writeText(e.search_query)}
                            className="rm-btn rm-btn-ghost text-[10px] px-2 py-1" title="Copy query">
                      <Copy size={11} />
                    </button>
                  </li>
                ))}
              </ul>
            </ResultSection>
          )}
          {data.first_move && (
            <ResultSection
              title="Your move this week"
              eyebrow="Do this in the next 7 days"
              accentGradient="linear-gradient(135deg,#10B981 0%,#047857 100%)"
              defaultOpen
              testid="events-first-move-section"
            >
              <div className="rm-card-static p-5" style={{ background: "linear-gradient(135deg,#FF6A00,#B45309)", color: "white" }}
                   data-testid="events-first-move">
                <p className="text-base md:text-lg leading-snug">{data.first_move}</p>
              </div>
            </ResultSection>
          )}
        </div>
      )}
    </div>
  );
}
